import { useState } from 'react'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { requestErrorMessage } from '@/helpers/apiClient'
import { formatMicros } from '@/lib/adFormat'
import { useAdRemovalPreview, useRemoveAdAccountFromVenue } from '@/hooks/useAds'
import type { AdAccountDto } from '@lib/dto/ads.dto'

interface RemoveAdAccountDialogProps {
  account: AdAccountDto
  onClose: () => void
}

/**
 * Removing an account from its venue deletes every number it brought in. It is
 * for an account assigned by mistake, so the confirmation says exactly which
 * venues lose how much, and points at stopping for keeping them.
 */
export function RemoveAdAccountDialog({ account, onClose }: RemoveAdAccountDialogProps) {
  const preview = useAdRemovalPreview(account.id)
  const remove = useRemoveAdAccountFromVenue()
  const [error, setError] = useState<string | null>(null)

  function describe() {
    if (preview.isPending) return 'Working out what it would delete…'
    if (preview.isError) return requestErrorMessage(preview.error)
    const { venues, campaignCount } = preview.data
    const losses = venues.map((v) => `${v.venueName} loses ${formatMicros(v.spendMicros, v.currencyCode)} of spend`)
    return (
      <>
        {account.name} goes back to waiting, and its {campaignCount === 1 ? 'campaign' : `${String(campaignCount)} campaigns`}{' '}
        and every number brought in for it are deleted: {losses.join('; ')}. To keep its numbers, stop it instead.
      </>
    )
  }

  function confirm() {
    setError(null)
    remove.mutate(account.id, { onSuccess: onClose, onError: (e) => setError(requestErrorMessage(e)) })
  }

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title="Remove from its venue?"
      description={describe()}
      confirmLabel="Remove and delete numbers"
      destructive
      busy={remove.isPending || preview.isPending}
      error={error}
      onConfirm={confirm}
    />
  )
}
